import { useState } from 'react'
import { useDeleteDeal, useSetArchived } from '../../api/queries/dealMutations'
import { apiDownload } from '../../api/client'
import { userFacingMessage } from '../../api/errors'
import { Button } from '../../ui/Button'
import { ConfirmDialog } from '../../ui/ConfirmDialog'
import type { DealDetail } from '../../api/adapters/dealDetail'
import styles from './DealPanel.module.css'

/**
 * Archiving and deleting, at the foot of the panel where the reversible actions
 * are not.
 *
 * Archive is reversible from the board's archived view, so it asks nothing.
 * Delete is not reversible anywhere in the product, so it asks, and the wording
 * says what actually happens rather than promising erasure the product does not
 * perform.
 *
 * The PDF sits above them: it is the one thing here you would do on purpose
 * rather than at the end of a deal's life, and it is assembled by the server
 * from the whole deal rather than from what this panel has loaded.
 */
export function DealActions({ deal, onDeleted }: { deal: DealDetail; onDeleted: () => void }) {
  const [confirming, setConfirming] = useState(false)
  const [exporting, setExporting] = useState(false)
  const [exportError, setExportError] = useState<string | null>(null)
  const archive = useSetArchived(deal.id)
  const remove = useDeleteDeal(deal.id)

  async function downloadPdf() {
    setExporting(true)
    setExportError(null)
    try {
      // Through the API client so the request carries the session; a plain
      // anchor href would arrive unauthenticated.
      const { blob, filename } = await apiDownload(`/deals/${deal.id}/export.pdf`)
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = filename
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      setTimeout(() => URL.revokeObjectURL(url), 1000)
    } catch (err) {
      setExportError(userFacingMessage(err))
    } finally {
      setExporting(false)
    }
  }

  return (
    <div className={styles.dangerZone}>
      <Button className={styles.wide} disabled={exporting} onClick={downloadPdf}>
        {exporting ? 'Preparing…' : 'Download as PDF'}
      </Button>
      {exportError ? (
        <p className={styles.failed} role="alert">
          The PDF was not created. {exportError}
        </p>
      ) : null}

      <Button
        className={styles.wide}
        disabled={archive.isPending}
        onClick={() => archive.mutate(!deal.archived)}
      >
        {archive.isPending ? 'Working…' : deal.archived ? 'Restore to the pipeline' : 'Archive this deal'}
      </Button>
      {archive.isError ? (
        <p className={styles.failed} role="alert">
          {deal.archived ? 'This deal was not restored. ' : 'This deal was not archived. '}
          {userFacingMessage(archive.error)}
        </p>
      ) : null}

      <Button className={`${styles.wide} ${styles.deleteButton}`} onClick={() => setConfirming(true)}>
        Delete this deal
      </Button>
      {remove.isError ? (
        <p className={styles.failed} role="alert">
          This deal was not deleted. {userFacingMessage(remove.error)}
        </p>
      ) : null}

      {confirming ? (
        <ConfirmDialog
          title={`Delete ${deal.companyName}?`}
          body={
            <>
              <p>
                The deal and its call history leave your pipeline and its reports. You cannot undo this in
                YourDealPulse.
              </p>
              <p>Archiving instead keeps the deal and takes it off the board.</p>
            </>
          }
          confirmLabel="Delete deal"
          pending={remove.isPending}
          onCancel={() => setConfirming(false)}
          onConfirm={() =>
            remove.mutate(undefined, {
              onSuccess: onDeleted,
              onError: () => setConfirming(false),
            })
          }
        />
      ) : null}
    </div>
  )
}
